import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { AbstractControl, FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { CustomValidators, StoreLoader, Unsubscriber } from '@core-lib/classes';
import { markFormGroupTouched } from '@core-lib/functions';
import { IMail, IMailForm } from '@core-lib/models';
import { IDocumentsFilesForm, UploadDocumentModalComponent } from '@core-lib/modules';
import { ReferentialsService } from '@core-lib/services';
import { ConfirmationService } from '@core-lib/services/confirmation-service.service';
import { MailService } from '@core-lib/services/mail.service';
import { NgSelectComponent } from '@ng-select/ng-select';
import { ModalRef, ModalService } from '@core-lib/modules/modal-container';
import { Subject, of, takeUntil } from 'rxjs';

@Component({
    selector: 'mail-modal',
    templateUrl: './mail-modal.component.html',
    styles: [],
    standalone: false
})
export class MailModalComponent extends Unsubscriber implements OnInit {
	@ViewChild(UploadDocumentModalComponent) uploadDocumentModal: UploadDocumentModalComponent;
	@ViewChild('ccSelect') ccSelectInput: NgSelectComponent;
	@Input() isAddModalWithoutType: boolean = false;
	@Input() set isFromFrontContactPage(value: boolean) {
		if (value) {
			this._isFromFrontContactPage = value;
			this.receiverField?.disable();
			this.form?.addControl('ccMe', new FormControl<boolean>(false));
		}
	}

	get isFromFrontContactPage(): boolean {
		return this._isFromFrontContactPage;
	}

	private _isFromFrontContactPage: boolean = false;
	@Input() senderList: Array<string> = [];
	@Output() emitSuccessEvent: EventEmitter<IMail> = new EventEmitter();

	_body: string;
	@Input() set body(value: string) {
		this._body = value;
		this.form?.get('body')?.patchValue(value);
	}
	get body(): string {
		return this._body;
	}

	_receiver: string;
	@Input() set receiver(value: string) {
		this._receiver = value;
		this.form?.get('receiver')?.patchValue(value);
	}
	get receiver(): string {
		return this._receiver;
	}

	_sender: string;
	@Input() set sender(value: string) {
		this._sender = value;
		this.form?.get('sender')?.patchValue(value);
	}
	get sender(): string {
		return this._sender;
	}

	_subject: string;
	@Input() set subject(value: string) {
		this._subject = value;
		this.subjectField?.patchValue(value);
	}
	get subject(): string {
		return this._subject;
	}

	_evenementId: string;
	@Input() set evenementId(value: string) {
		if (value) {
			this._evenementId = value;
			this.form?.controls?.evenementId?.patchValue(value);
			this.initCCDropdown();
		}
	}
	get evenementId(): string {
		return this._evenementId;
	}

	_courrierId: string;
	@Input() set courrierId(value: string) {
		this._courrierId = value;
		this.form?.controls?.courrierId.patchValue(value);
		this.initCCDropdown();
	}
	get courrierId(): string {
		return this._courrierId;
	}

	_courrierFile: File;
	@Input() set courrierFile(value: File) {
		this._courrierFile = value;
	}
	get courrierFile(): File {
		return this._courrierFile;
	}

	_compteId: string;
	@Input() set compteId(value: string) {
		this._compteId = value;
		this.form?.controls?.compteId?.patchValue(value);
	}

	get compteId(): string {
		return this._compteId;
	}

	_affaireId: string;
	@Input() set affaireId(value: string) {
		this._affaireId = value;
		this.form?.controls?.affaireId?.patchValue(value);
	}

	get affaireId(): string {
		return this._affaireId;
	}

	form: FormGroup<IMailForm>;

	get senderField(): AbstractControl {
		return this.form?.get('sender');
	}

	get receiverField(): AbstractControl {
		return this.form?.get('receiver');
	}

	get subjectField(): AbstractControl {
		return this.form?.get('subject');
	}

	get bodyField(): AbstractControl {
		return this.form?.get('body');
	}

	get attachedDocumentField(): AbstractControl {
		return this.form?.get('attachedDocument');
	}

	get ccRecieversFormArray(): FormArray {
		return this.form?.get('ccReceivers') as FormArray;
	}

	predefinedEmails: string[] = [];
	isCCEMailsOneOrManyError: boolean = false;

	mailLoader: StoreLoader = new StoreLoader(of(false), of(null));

	mailLoaderSubject = new Subject<StoreLoader>();
	mailLoader$ = this.mailLoaderSubject.asObservable();

	constructor(
		public modalRef: ModalRef,
		private mailService: MailService,
		private referentialsService: ReferentialsService,
		private modalService: ModalService,
		private confirmationService: ConfirmationService
	) {
		super();
		this.mailLoader$.subscribe(res => (this.mailLoader = res));
	}

	ngOnInit(): void {
		this.buildForm();
	}

	initCCDropdown(): void {
		this.referentialsService
			.getAvocatsCC()
			.pipe(takeUntil(this.destroySubscriptions$))
			.subscribe(res => {
				this.predefinedEmails = res;
				if (res?.length > 0) {
					this.addCCReceiversControl(res[0]);
					this.checkCCEmailsValidity();
					res?.forEach(element => {
						this.ccSelectInput.itemsList.addItem(element);
					});
					if (this.ccSelectInput?.itemsList?.items?.length > 0) {
						this.ccSelectInput.itemsList.select(this.ccSelectInput.itemsList.items[0]);
					}
				}
			});
	}

	buildForm(): void {
		this.form = new FormGroup<IMailForm>({
			sender: new FormControl<string>({ value: this.sender, disabled: false }, [Validators.required, CustomValidators.formatEmailNotification()]),
			receiver: new FormControl<string>(this.receiver, [Validators.required, CustomValidators.formatEmailNotification()]),
			subject: new FormControl<string>(null, [Validators.required, CustomValidators.maximumLength(200)]),
			ccReceivers: new FormArray<FormControl<string>>([]),
			body: new FormControl<string>(this.body, Validators.required),
			attachedDocument: new FormControl<IDocumentsFilesForm>(null),
			evenementId: new FormControl<string>(this.evenementId),
			courrierId: new FormControl<string>(this.courrierId),
			compteId: new FormControl<string | null>(this.compteId),
			affaireId: new FormControl<string | null>(this.affaireId)
		});
	}

	addCCReceiversControl(value: string): void {
		this.ccRecieversFormArray.push(new FormControl<string>(value));
	}

	onAddCCReceiver(event: any): void {
		const emailValue = this.getNgSelectEventValue(event);
		this.addCCReceiversControl(emailValue);
		this.checkCCEmailsValidity();
	}

	onRemoveCCReceiver(event: any): void {
		const emailValue = this.getNgSelectEventValue(event);
		const emailControlToDeleteIndex = this.ccRecieversFormArray?.controls?.findIndex(email => email.value === emailValue);
		this.ccRecieversFormArray.removeAt(emailControlToDeleteIndex);
		this.checkCCEmailsValidity();
	}

	/**
	 * Action fermeture popup: ANNULATION
	 */
	onClose(): void {
		this.modalRef.hide();
		this.mailLoaderSubject.next(new StoreLoader(of(false), of(null)));
		if (this.courrierId) {
			this.mailService
				.deleteCourrier(this.courrierId)
				.pipe(takeUntil(this.destroySubscriptions$))
				.subscribe({
					next: () => { },
					error: () => { }
				});
		}
	}

	/**
	 * Action confirmation
	 */
	onConfirm(): void {
		//docs to delete
		markFormGroupTouched(this.form);
		if (this.form.valid && (this.ccRecieversFormArray?.controls?.length === 0 || !this.isCCEMailsOneOrManyError)) {
			this.bodyField.patchValue(this.bodyField.value?.replace(/\n/g, '<br/>'));
			this.attachedDocumentField?.patchValue(null);
			this.mailLoaderSubject.next(new StoreLoader(of(true), of(null)));
			//list des element ajouter non encore envoyé à alfresco
			let attachedDocumentForEndMail = this.uploadDocumentModal.formTemps.value;

			//ajouter la liste des doc qui sont déjà dans alfresco
			this.uploadDocumentModal?.gedDocs?.forEach(element => {
				attachedDocumentForEndMail.filesList.push(element);
			});
			this.attachedDocumentField?.patchValue(attachedDocumentForEndMail);

			if (this.affaireId || this.compteId) {
				this.mailService
					.sendMailContactFO(this.form.getRawValue())
					.pipe(takeUntil(this.destroySubscriptions$))
					.subscribe({
						next: res => {
							if (res) {
								this.mailLoaderSubject.next(new StoreLoader(of(false), of(null)));
								this.emitSuccessEvent.emit(this.form.value as IMail);
								this.modalRef.hide();
							}
						},
						error: () => {
							this.bodyField.patchValue(this.bodyField.value?.replace(/<br\s*\/?>/g, '\n'));
							this.mailLoaderSubject.next(new StoreLoader(of(false), of(null)));
							this.openErrorSaisieOperationModal();
						}
					});
			} else {
				this.mailService.sendMailWithAttachedFiles(this.form.value).subscribe({
					next: res => {
						if (res) {
							this.mailLoaderSubject.next(new StoreLoader(of(false), of(null)));
							this.emitSuccessEvent.emit(this.form.value as IMail);
							this.modalRef.hide();
						}
					},
					error: () => {
						this.bodyField.patchValue(this.bodyField.value?.replace(/<br\s*\/?>/g, '\n'));
						this.mailLoaderSubject.next(new StoreLoader(of(false), of(null)));
						this.openErrorSaisieOperationModal();
					}
				});
			}
		}
	}

	private openErrorSaisieOperationModal(): void {
		this.confirmationService.openConfirmationModal({
			canClose: false,
			textsKey: `EnvoiMailError`,
			confirmBtnText: 'Actions.Close'
		});
	}

	private checkCCEmailsValidity(): void {
		this.isCCEMailsOneOrManyError = this.ccRecieversFormArray?.controls?.some(el => !this.isValidEmail(el?.value) || false);
	}

	private getNgSelectEventValue(value: any): string {
		return typeof value === 'object' ? value?.label : typeof value === 'string' ? value : '';
	}

	private isValidEmail(email: string): boolean {
		const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
		return emailRegex.test(email);
	}
}
